import { useCallback, useEffect, useState, type ChangeEvent } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControlLabel,
  Paper,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import {
  createWebsitePopup,
  deleteWebsitePopup,
  getWebsitePopup,
  updateWebsiteBanner,
  WebsitePopup,
} from "../../apis/websitePopupApi";

const WebsitePopupAdmin = () => {
  const theme = useTheme();
  const [popup, setPopup] = useState<WebsitePopup | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [savingBanner, setSavingBanner] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [bannerText, setBannerText] = useState("");
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  const showMessage = (message: string, severity: "success" | "error") => {
    setSnackbar({ open: true, message, severity });
  };

  const applyLayout = (data: WebsitePopup | null) => {
    setPopup(data);
    setBannerText(data?.bannerText ?? "");
    setBannerEnabled(Boolean(data?.bannerEnabled && data?.bannerText));
  };

  const loadPopup = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getWebsitePopup();
      applyLayout(data);
    } catch (error: any) {
      console.error(error);
      showMessage(
        error?.response?.data?.message || "Failed to load website layout",
        "error"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPopup();
  }, [loadPopup]);

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (preview) {
      URL.revokeObjectURL(preview);
    }
    if (!file) {
      setSelectedFile(null);
      setPreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      showMessage("Please select an image file", "error");
      event.target.value = "";
      return;
    }
    setSelectedFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showMessage("Please select an image first", "error");
      return;
    }
    setUploading(true);
    try {
      const created = await createWebsitePopup(selectedFile);
      applyLayout(created);
      if (preview) URL.revokeObjectURL(preview);
      setSelectedFile(null);
      setPreview(null);
      showMessage(
        popup?.imageUrl
          ? "Popup replaced successfully"
          : "Popup uploaded successfully",
        "success"
      );
    } catch (error: any) {
      console.error(error);
      showMessage(
        error?.response?.data?.message || "Failed to upload popup image",
        "error"
      );
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    if (!popup?.imageUrl) return;
    setRemoving(true);
    try {
      const updated = await deleteWebsitePopup();
      applyLayout(updated);
      showMessage("Popup removed successfully", "success");
    } catch (error: any) {
      console.error(error);
      showMessage(
        error?.response?.data?.message || "Failed to remove popup",
        "error"
      );
    } finally {
      setRemoving(false);
    }
  };

  const handleSaveBanner = async () => {
    setSavingBanner(true);
    try {
      const updated = await updateWebsiteBanner({
        bannerText: bannerText.trim() || null,
        bannerEnabled: bannerEnabled && Boolean(bannerText.trim()),
      });
      applyLayout(updated);
      showMessage("Offer banner saved", "success");
    } catch (error: any) {
      console.error(error);
      showMessage(
        error?.response?.data?.message || "Failed to save offer banner",
        "error"
      );
    } finally {
      setSavingBanner(false);
    }
  };

  const handleClearBanner = async () => {
    setSavingBanner(true);
    try {
      const updated = await updateWebsiteBanner({
        bannerText: null,
        bannerEnabled: false,
      });
      applyLayout(updated);
      showMessage("Offer banner cleared", "success");
    } catch (error: any) {
      console.error(error);
      showMessage(
        error?.response?.data?.message || "Failed to clear offer banner",
        "error"
      );
    } finally {
      setSavingBanner(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Website Layout
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage the homepage offer ticker and the site-wide popup image.
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={loadPopup}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={3}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Homepage offer banner
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Scrolling text shown above the navbar on the homepage only.
                Turn it off when the offer ends.
              </Typography>

              <Stack spacing={2}>
                <TextField
                  label="Offer text"
                  placeholder="e.g. Anniversary sale — 25% off bookings this August!"
                  value={bannerText}
                  onChange={(e) => setBannerText(e.target.value)}
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 280 }}
                  helperText={`${bannerText.length}/280`}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={bannerEnabled}
                      onChange={(_, checked) => setBannerEnabled(checked)}
                      disabled={!bannerText.trim()}
                    />
                  }
                  label="Show on homepage"
                />
                <Stack direction="row" spacing={1.5}>
                  <Button
                    variant="contained"
                    onClick={handleSaveBanner}
                    disabled={savingBanner}
                    startIcon={
                      savingBanner ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SaveIcon />
                      )
                    }
                  >
                    Save banner
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleClearBanner}
                    disabled={savingBanner || (!popup?.bannerText && !bannerText)}
                    startIcon={<DeleteIcon />}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Current popup
              </Typography>
              {popup?.imageUrl ? (
                <Stack spacing={2}>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.common.black, 0.02),
                    }}
                  >
                    <Box
                      component="img"
                      src={popup.imageUrl}
                      alt="Current website popup"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 420,
                        width: "auto",
                        height: "auto",
                        objectFit: "contain",
                        borderRadius: 1,
                      }}
                    />
                  </Paper>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={
                      removing ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteIcon />
                      )
                    }
                    onClick={handleRemove}
                    disabled={removing || uploading}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Remove popup
                  </Button>
                </Stack>
              ) : (
                <Alert severity="info" icon={<ImageIcon />}>
                  No popup is active. Upload an image below to show one on the
                  website.
                </Alert>
              )}
            </CardContent>
          </Card>

          <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                {popup?.imageUrl ? "Replace popup image" : "Upload popup image"}
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Only one popup image is kept at a time. Uploading a new image
                replaces the previous one without clearing the offer banner.
              </Typography>

              <Stack spacing={2}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Choose image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </Button>

                {preview && (
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "center",
                      bgcolor: alpha(theme.palette.common.black, 0.02),
                    }}
                  >
                    <Box
                      component="img"
                      src={preview}
                      alt="Preview"
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 320,
                        objectFit: "contain",
                        borderRadius: 1,
                      }}
                    />
                  </Paper>
                )}

                <Button
                  variant="contained"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading || removing}
                  startIcon={
                    uploading ? (
                      <CircularProgress size={18} color="inherit" />
                    ) : (
                      <CloudUploadIcon />
                    )
                  }
                  sx={{ alignSelf: "flex-start" }}
                >
                  {popup?.imageUrl ? "Replace popup" : "Upload popup"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default WebsitePopupAdmin;
